import React, { useRef, useState } from "react";
import * as XLSX from "xlsx";
import { api } from "../../lib/api";
import {
  Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { UploadSimple, Trophy, Trash } from "@phosphor-icons/react";

// Normalize a header label to a stable key for column lookup.
const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

// Column aliases — every reasonable spelling the user might put in the header.
const COL_ALIASES = {
  first: ["firstname", "first", "fname"],
  last: ["lastname", "last", "lname", "surname"],
  premium: [
    "totalpaidlifedisabilityltctargetpremium",
    "totalpaidlifedisltctargetpremium",
    "totallifediltcpremium",
    "totallifedisabilityltcpremium",
    "totallifedisltcpremium",
    "lifedisabilityltcpremium",
    "lifedisltcpremium",
    "lifediltcpremium",
    "lifedisltc",
    "lifedisabilityltc",
    "targetpremium",
    "premium",
  ],
  cases: [
    "paidcases", "casespaid", "totalpaidcases", "cases", "paidcasecount",
    "totallifediltccases", "totallifedisabilityltccases", "totallifedisltccases",
    "lifediltccases", "lifedisltccases",
  ],
  gdc: [
    "totalpremiumandgdc", "totalpremiumgdc", "premiumandgdc",
    "totalgdc", "gdc", "ytdgdc", "gdcytd",
    "osaicgdc", "osaicgdcytd", "osaic2026gdcytd",
  ],
  cfnstart: ["cfnstartdate", "cfnstart", "startdate", "started", "joindate", "yearstarted"],
};

// Fallback heuristics when no exact alias hits — match by KEYWORD PRESENCE.
// Order matters: first matching column wins. All `needs` substrings must
// appear in the normalized header.
const COL_HEURISTICS = {
  premium: [
    { needs: ["premium"], avoids: ["gdc"] }, // anything with "premium" but not "gdc"
  ],
  cases: [
    { needs: ["cases"] },
    { needs: ["case"] }, // singular fallback ("Case Count", "Case Total" etc.)
  ],
  gdc: [
    { needs: ["totalpremium", "gdc"] }, // prefer "Total Premium & GDC" rollup column
    { needs: ["totalgdc"] },
    { needs: ["gdc"] },
  ],
  cfnstart: [
    { needs: ["start"], avoids: ["earnings"] },
  ],
};

function findColIndex(headerRow, key) {
  const aliases = COL_ALIASES[key] || [];
  const normalized = headerRow.map((h) => norm(h));
  // 1. Exact alias match — iterate aliases in priority order so the FIRST
  //    alias in the list wins even if a later column also matches a later alias.
  for (const alias of aliases) {
    const idx = normalized.indexOf(alias);
    if (idx >= 0) return idx;
  }
  // 2. Heuristic match (substring-based)
  const rules = COL_HEURISTICS[key] || [];
  for (const rule of rules) {
    for (let i = 0; i < normalized.length; i += 1) {
      const h = normalized[i];
      if (!h) continue;
      const needs = rule.needs || [];
      const avoids = rule.avoids || [];
      if (avoids.some((a) => h.includes(a))) continue;
      if (needs.every((n) => h.includes(n))) {
        // For premium specifically, avoid mapping the same column as the GDC rollup.
        if (key === "premium" && h.includes("gdc") && !h.includes("premium")) continue;
        return i;
      }
    }
  }
  return -1;
}

const numberish = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  const cleaned = String(v).replace(/[$,\s]/g, "");
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : 0;
};

export default function LeadersBulkDialog({ onUploaded }) {
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState([]); // parsed preview rows
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [existing, setExisting] = useState([]);
  const fileRef = useRef(null);

  const reset = () => {
    setRows([]);
    setResult(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const loadExisting = async (y) => {
    try {
      const { data } = await api.get(`/awards/leaders/data?year=${y}`);
      setExisting(data.rows || []);
    } catch {
      setExisting([]);
    }
  };

  React.useEffect(() => {
    if (open) loadExisting(year);
  }, [open, year]);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setResult(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = new Uint8Array(ev.target.result);
        const wb = XLSX.read(data, { type: "array" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", blankrows: false });
        if (matrix.length < 2) {
          toast.error("Spreadsheet is empty");
          return;
        }
        const header = matrix[0];
        const idx = {
          first: findColIndex(header, "first"),
          last: findColIndex(header, "last"),
          premium: findColIndex(header, "premium"),
          cases: findColIndex(header, "cases"),
          gdc: findColIndex(header, "gdc"),
          cfnstart: findColIndex(header, "cfnstart"),
        };
        if (idx.first < 0 || idx.last < 0) {
          toast.error("Missing required columns: First Name / Last Name");
          return;
        }
        const parsed = [];
        for (let r = 1; r < matrix.length; r += 1) {
          const row = matrix[r];
          const first = String(row[idx.first] || "").trim();
          const last = String(row[idx.last] || "").trim();
          if (!first && !last) continue; // skip blank
          // CFN Start Date — accept Excel serial dates or strings
          let cfnStart = "";
          if (idx.cfnstart >= 0) {
            const v = row[idx.cfnstart];
            if (v instanceof Date) {
              cfnStart = v.toISOString().slice(0, 10);
            } else if (typeof v === "number" && v > 0) {
              // Excel serial — XLSX gives JS numbers when cellDates: false
              const d = XLSX.SSF.parse_date_code(v);
              if (d) cfnStart = `${d.y}-${String(d.m).padStart(2, "0")}-${String(d.d).padStart(2, "0")}`;
            } else if (v) {
              cfnStart = String(v).trim();
            }
          }
          parsed.push({
            first_name: first,
            last_name: last,
            cfn_start_date: cfnStart || undefined,
            life_dis_ltc_premium: idx.premium >= 0 ? numberish(row[idx.premium]) : 0,
            paid_cases: idx.cases >= 0 ? Math.round(numberish(row[idx.cases])) : 0,
            total_gdc: idx.gdc >= 0 ? numberish(row[idx.gdc]) : 0,
          });
        }
        if (!parsed.length) {
          toast.error("No valid rows found");
          return;
        }
        setRows(parsed);
        toast.success(`Parsed ${parsed.length} ${parsed.length === 1 ? "row" : "rows"} — review and submit.`);
      } catch (err) {
        toast.error(err.message || "Could not parse file");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const submit = async () => {
    if (!rows.length) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/awards/leaders/bulk", { year, entries: rows });
      setResult(data);
      const total = (data.inserted || 0) + (data.updated || 0);
      toast.success(`${total} agent${total === 1 ? "" : "s"} updated for ${data.year}${data.skipped?.length ? ` · ${data.skipped.length} skipped` : ""}`);
      loadExisting(year);
      onUploaded?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setSubmitting(false);
    }
  };

  const removeRow = async (row) => {
    if (!window.confirm("Remove this Leader's Conference upload? They'll revert to activity-driven progress (or vanish from the race if external).")) return;
    try {
      const key = row.is_external ? `external:${(row.external_first || "").toLowerCase()}:${(row.external_last || "").toLowerCase()}` : row.agent_id;
      await api.delete(`/awards/leaders/data/${encodeURIComponent(key)}?year=${year}`);
      toast.success("Removed");
      loadExisting(year);
      onUploaded?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button
          className="rounded-none bg-white text-black border-2 border-black hover:bg-[#fef3c7] shadow-[4px_4px_0_0_#09090b] font-bold uppercase tracking-widest text-xs h-11"
          data-testid="bulk-leaders-btn"
        >
          <Trophy size={14} weight="bold" className="mr-1" /> Bulk Leaders
        </Button>
      </DialogTrigger>
      <DialogContent className="border-2 border-black rounded-none shadow-[6px_6px_0_0_#09090b] max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-display font-black tracking-tight">Bulk Update Leader&apos;s Conference</DialogTitle>
          <DialogDescription className="text-zinc-600">
            Upload a spreadsheet to set every agent&apos;s YTD numbers for the Leader&apos;s Conference race.
            One row per agent. Re-uploading overwrites prior values for the same year. The activity log is not touched.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="font-bold uppercase tracking-widest text-[10px]">Year</Label>
              <Input
                type="number"
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value, 10) || new Date().getFullYear())}
                className="mt-1 border-2 border-black rounded-none h-10 focus-visible:ring-0"
                data-testid="leaders-year-input"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="font-bold uppercase tracking-widest text-[10px]">Spreadsheet (.xlsx or .csv)</Label>
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFile}
                className="mt-1 w-full border-2 border-black h-10 px-3 text-sm file:mr-3 file:border-0 file:bg-[#fef3c7] file:px-3 file:py-2 file:font-bold file:uppercase file:tracking-widest file:text-xs"
                data-testid="leaders-file-input"
              />
            </div>
          </div>

          <div className="brut bg-[#f4f4f5] p-3 text-xs text-zinc-700">
            <span className="font-bold uppercase tracking-widest mr-2">Required columns</span>
            <code className="text-[11px]">first name</code>, <code className="text-[11px]">last name</code>
            <span className="font-bold uppercase tracking-widest mx-2">+ optional</span>
            <code className="text-[11px]">total paid life dis ltc target premium</code>,
            <code className="text-[11px]"> paid cases</code>,
            <code className="text-[11px]"> total gdc</code>,
            <code className="text-[11px]"> cfn start date</code>
            <div className="mt-1 text-[11px] text-zinc-500">
              Header spelling is flexible (case + punctuation ignored). Empty columns are treated as 0.
              Agents who are not registered users will be added as <span className="font-bold">EXTERNAL</span> entries — set their CFN start date so the system picks the right tier. They auto-link to a real user if they later register with the same name.
            </div>
          </div>

          {/* Parsed preview */}
          {rows.length > 0 && (
            <div className="brut bg-white max-h-64 overflow-auto" data-testid="leaders-preview">
              <table className="w-full border-collapse text-sm">
                <thead className="sticky top-0 bg-[#09090b] text-white">
                  <tr>
                    <th className="text-left px-2 py-2 font-bold uppercase text-[10px] tracking-widest">Agent</th>
                    <th className="text-right px-2 py-2 font-bold uppercase text-[10px] tracking-widest">Life+Dis+LTC $</th>
                    <th className="text-right px-2 py-2 font-bold uppercase text-[10px] tracking-widest">Paid Cases</th>
                    <th className="text-right px-2 py-2 font-bold uppercase text-[10px] tracking-widest">Total GDC $</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={`${r.first_name}-${r.last_name}-${i}`} className="border-b border-zinc-200 last:border-b-0">
                      <td className="px-2 py-1.5 font-bold">{r.first_name} {r.last_name}</td>
                      <td className="px-2 py-1.5 text-right">${r.life_dis_ltc_premium.toLocaleString()}</td>
                      <td className="px-2 py-1.5 text-right">{r.paid_cases}</td>
                      <td className="px-2 py-1.5 text-right">${r.total_gdc.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {result && (
            <div className="brut bg-[#ecfdf5] p-3 text-sm" data-testid="leaders-result">
              <div className="font-bold">Imported for {result.year}: {result.inserted} new · {result.updated} updated · {result.skipped?.length || 0} skipped</div>
              {result.skipped?.length > 0 && (
                <ul className="mt-2 text-xs text-zinc-700 list-disc list-inside">
                  {result.skipped.slice(0, 8).map((s, i) => (
                    <li key={`skip-${i}`}>
                      {(s.row.first_name || "") + " " + (s.row.last_name || "") || s.row.agent_email_or_id} — {s.reason}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Existing uploads for the year */}
          {existing.length > 0 && (
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-1">
                Current {year} uploads — {existing.length} {existing.length === 1 ? "agent" : "agents"}
              </div>
              <div className="brut bg-white max-h-56 overflow-auto" data-testid="leaders-existing">
                <table className="w-full border-collapse text-sm">
                  <thead className="sticky top-0 bg-[#09090b] text-white">
                    <tr>
                      <th className="text-left px-2 py-2 font-bold uppercase text-[10px] tracking-widest">Agent</th>
                      <th className="text-right px-2 py-2 font-bold uppercase text-[10px] tracking-widest">Life+Dis+LTC</th>
                      <th className="text-right px-2 py-2 font-bold uppercase text-[10px] tracking-widest">Cases</th>
                      <th className="text-right px-2 py-2 font-bold uppercase text-[10px] tracking-widest">GDC</th>
                      <th className="px-2 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {existing.map((r) => {
                      const rowKey = r.is_external ? `ext:${r.external_first}:${r.external_last}` : r.agent_id;
                      return (
                        <tr key={rowKey} className="border-b border-zinc-200 last:border-b-0">
                          <td className="px-2 py-1.5 font-bold">
                            {r.agent_name}
                            {r.is_external && (
                              <span className="ml-2 inline-flex items-center px-1.5 py-0.5 border-2 border-black text-[9px] font-black uppercase tracking-widest bg-[#fef3c7]" title="Not a registered user — auto-links if they register later">
                                EXT
                              </span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-right">${Number(r.life_dis_ltc_premium || 0).toLocaleString()}</td>
                          <td className="px-2 py-1.5 text-right">{r.paid_cases}</td>
                          <td className="px-2 py-1.5 text-right">${Number(r.total_gdc || 0).toLocaleString()}</td>
                          <td className="px-2 py-1.5 text-right">
                            <button
                              onClick={() => removeRow(r)}
                              className="border-2 border-black p-1 hover:bg-[#fb7185] hover:text-white"
                              title="Remove upload"
                              data-testid={`leaders-delete-${rowKey}`}
                            >
                              <Trash size={12} weight="bold" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-none bg-white text-black border-2 border-black hover:bg-zinc-100 font-bold uppercase tracking-widest text-xs h-10"
              data-testid="leaders-cancel"
            >Close</Button>
            <Button
              type="button"
              onClick={submit}
              disabled={rows.length === 0 || submitting}
              className="rounded-none bg-[#09090b] text-white border-2 border-black hover:bg-black shadow-[4px_4px_0_0_#fef3c7] hover:shadow-[6px_6px_0_0_#fef3c7] font-bold uppercase tracking-widest text-xs h-10 disabled:opacity-40"
              data-testid="leaders-submit"
            >
              <UploadSimple size={14} weight="bold" className="mr-1" />
              {submitting ? "Uploading…" : `Update ${rows.length || ""} ${rows.length === 1 ? "agent" : "agents"}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
